/**
 * Shared Google Maps JS loader.
 *
 * A single module-level Promise is reused across LiveMap, NearestStoresMap and
 * AddressAutocomplete so multiple components mounting on the same page can't
 * race and inject the maps script twice — that's what produced the persistent
 * "Google Maps JavaScript API has been loaded multiple times on this page"
 * warning.
 */
const SCRIPT_ID = '__google_maps_js__';
let promise = null;

// Google calls window.gm_authFailure once when the key/billing is invalid.
// Record it globally + broadcast so consumers (AddressAutocomplete) can fall
// back to OSM even if they mount after the failure already happened.
if (typeof window !== 'undefined' && !window.gm_authFailure) {
  window.gm_authFailure = () => {
    window.__gmapsAuthFailed = true;
    try { window.dispatchEvent(new Event('gmaps-auth-failed')); } catch (e) { /* */ }
  };
}

export const gmapsAuthFailed = () => (typeof window !== 'undefined' && !!window.__gmapsAuthFailed);

export const loadGoogleMaps = (apiKey) => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.google?.maps?.Map) return Promise.resolve(window.google);
  if (!apiKey) return Promise.resolve(null);
  if (promise) return promise;

  promise = new Promise((res, rej) => {
    // If some other bundle already injected the tag, just poll for readiness.
    const existing = document.getElementById(SCRIPT_ID);
    const settle = () => {
      const start = Date.now();
      const tick = () => {
        if (window.google?.maps?.Map) return res(window.google);
        if (Date.now() - start > 8000) return rej(new Error('Google Maps not ready'));
        setTimeout(tick, 50);
      };
      tick();
    };
    if (existing) { settle(); return; }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.async = true; s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
    s.onload = () => {
      settle();
      // If the key's billing is disabled Google injects a blocking "can't load
      // Google Maps correctly" dialog. Auto-dismiss it — the app falls back to
      // OSM address search, so the dialog is just noise.
      let tries = 0;
      const t = setInterval(() => {
        tries += 1;
        const btn = document.querySelector('button.dismissButton');
        if (btn) { btn.click(); clearInterval(t); }
        if (tries > 60) clearInterval(t);
      }, 500);
    };
    s.onerror = (e) => { promise = null; rej(e); };
    document.head.appendChild(s);
  });
  return promise;
};

export default loadGoogleMaps;
